/**
 * Pure helpers for the `form` core view (ported from the MEIS-13490
 * prototype's `agentFormSpec.ts`): validating a spec that came over the wire
 * before rendering it, client-side field validation, changed-vs-previous
 * detection, the human-readable answer text and machine payload a submit
 * sends. `formViewMemory` is not ported: the backend sets `view.memory`
 * itself (see the views registry doc).
 *
 * Transport for the machine payload: the exact submitted values travel as a
 * trailing fenced code block (```agent-form … ```) appended to the
 * human-readable answer text — not a separate chat data part. The backend
 * already reads a plain `content` string per message, so the block reaches
 * the model with zero adapter changes; only the *display* of a user message
 * needs to strip it, which `AgentChatBox.tsx`'s text-part renderer does via
 * `stripFormAnswerBlock` for the user role. The backend locates the same
 * fence in the last user message's content and parses the JSON object for
 * exact values, rather than re-parsing the human-readable line.
 *
 * `validateFormView`'s problems are internal diagnostics (never shown to the
 * user — a malformed view renders a single generic warning instead, like
 * `agentChoiceSpec.ts`'s `validateChoiceView`). `validateFieldValue`'s and
 * `validateAddressValue`'s messages ARE shown as a field's `helperText`, so
 * they are themselves Tolgee keys (English text) the component translates —
 * see `AgentFormView.tsx`.
 */
import type { AgentViewSpec } from "./registry.js";

export type Address = {
  street: string;
  postalCode: string;
  city: string;
  country: string;
  /** Coordinates from the address search (Google Places), like the CRM dialogs store; absent when typed or prefilled. */
  location?: { lat: number; lng: number } | null;
};

export type FormFieldType =
  | "text"
  | "textarea"
  | "email"
  | "phone"
  | "number"
  | "date"
  | "select"
  | "multiselect"
  | "address";

export type FormFieldValue = string | number | string[] | Address | null;

export type FormFieldOption = { value: string; label: string };

export type FormField = {
  key: string;
  label: string;
  type: FormFieldType;
  required: boolean;
  value: FormFieldValue;
  previous: FormFieldValue;
  options: FormFieldOption[] | null;
  help: string | null;
};

export type FormViewSpec = {
  component: "form";
  title: string;
  description: string | null;
  fields: FormField[];
  submitLabel: string;
  confirmsWrite: boolean;
};

const MIN_FIELDS = 1;
const MAX_FIELDS = 20;
const FIELD_TYPES: readonly FormFieldType[] = [
  "text",
  "textarea",
  "email",
  "phone",
  "number",
  "date",
  "select",
  "multiselect",
  "address",
];
/** Shown as a field's `helperText` — English text, itself a Tolgee key (see the module doc). */
export const REQUIRED_MESSAGE = "Required";
export const INVALID_EMAIL_MESSAGE = "Invalid email address";
export const INVALID_PHONE_MESSAGE =
  "Only digits, spaces and + - / ( ) allowed";
export const INVALID_DATE_MESSAGE = "Date expected as YYYY-MM-DD";
export const INVALID_NUMBER_MESSAGE = "Number expected";
export const INVALID_POSTAL_CODE_MESSAGE = "Postal code: 5 digits expected";
export const ADDRESS_NOT_FROM_SEARCH_MESSAGE =
  "Please select an address from the search";
const DE_POSTAL_CODE_RE = /^\d{5}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[0-9+\-/()\s]+$/;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const NUMBER_RE = /^-?\d+(\.\d+)?$/;

export function isAddressValue(value: FormFieldValue): value is Address {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    "street" in value &&
    "postalCode" in value &&
    "city" in value &&
    "country" in value
  );
}

function isGermanCountry(country: string): boolean {
  const normalized = country.trim().toUpperCase();
  return normalized === "DE" || normalized === "DEUTSCHLAND";
}

function valueMatchesType(type: FormFieldType, value: FormFieldValue): boolean {
  if (value === null) return true;
  switch (type) {
    case "multiselect":
      return Array.isArray(value) && value.every(v => typeof v === "string");
    case "address":
      return isAddressValue(value);
    case "number":
      return typeof value === "number";
    default:
      return typeof value === "string";
  }
}

/**
 * Every problem with the spec, so a malformed view can be reported at once
 * instead of crashing on the first bad access. Empty means the spec is safe
 * to render. `view` is the loosely typed value that came in over the wire
 * (`AgentViewSpec`) — not yet trusted to be a `FormViewSpec`.
 */
function isUnknownRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export function validateFormView(view: AgentViewSpec): string[] {
  const problems: string[] = [];

  if (typeof view.title !== "string" || view.title.trim() === "")
    problems.push("title: must not be empty");
  if (typeof view.submitLabel !== "string" || view.submitLabel.trim() === "")
    problems.push("submitLabel: must not be empty");

  const fields = view.fields;
  if (
    !Array.isArray(fields) ||
    fields.length < MIN_FIELDS ||
    fields.length > MAX_FIELDS
  ) {
    problems.push(
      `fields: expected ${MIN_FIELDS} to ${MAX_FIELDS} entries, got ${
        Array.isArray(fields) ? fields.length : 0
      }`
    );
    return problems;
  }

  const seenKeys = new Set<string>();
  fields.forEach((rawField: unknown, index: number) => {
    const position = index + 1;
    if (!isUnknownRecord(rawField)) {
      problems.push(`field ${position}: not an object`);
      return;
    }
    const field = rawField as Partial<FormField>;
    if (typeof field.key !== "string" || field.key.trim() === "") {
      problems.push(`field ${position}: key must not be empty`);
      return;
    }
    const tag = `field ${position} ("${field.key}")`;
    if (seenKeys.has(field.key)) problems.push(`${tag}: duplicate key`);
    seenKeys.add(field.key);

    if (typeof field.label !== "string" || field.label.trim() === "")
      problems.push(`${tag}: label must not be empty`);

    if (typeof field.type !== "string" || !FIELD_TYPES.includes(field.type))
      problems.push(`${tag}: unknown type "${String(field.type)}"`);

    if (field.type === "select" || field.type === "multiselect") {
      if (!Array.isArray(field.options) || field.options.length === 0) {
        problems.push(`${tag}: options expected`);
      } else {
        const seenValues = new Set<string>();
        field.options.forEach((rawOption: unknown) => {
          if (!isUnknownRecord(rawOption)) {
            problems.push(`${tag}: an option is not an object`);
            return;
          }
          const value = rawOption.value;
          const label = rawOption.label;
          if (typeof value !== "string" || value === "")
            problems.push(`${tag}: an option has no value`);
          else if (seenValues.has(value))
            problems.push(`${tag}: duplicate value "${value}"`);
          else seenValues.add(value);
          if (typeof label !== "string" || label.trim() === "")
            problems.push(`${tag}: an option has no label`);
        });
      }
    } else if (field.options !== null && field.options !== undefined) {
      problems.push(`${tag}: options only allowed for select/multiselect`);
    }

    if (
      typeof field.type === "string" &&
      !valueMatchesType(field.type, field.value ?? null)
    )
      problems.push(`${tag}: value does not match type "${field.type}"`);
    if (
      typeof field.type === "string" &&
      !valueMatchesType(field.type, field.previous ?? null)
    )
      problems.push(`${tag}: previous does not match type "${field.type}"`);
  });

  return problems;
}

/** Whether the spec is safe to hand to the form component as-is. */
export function isFormViewValid(view: AgentViewSpec): boolean {
  return validateFormView(view).length === 0;
}

/**
 * Every problem with a required address, a wrong German postal code, and an
 * address not picked from the address search (no coordinates) — per part.
 */
export function validateAddressValue(
  address: Address | null,
  required: boolean
): Partial<Record<keyof Address, string>> {
  const errors: Partial<Record<keyof Address, string>> = {};
  const a: Address = address ?? {
    street: "",
    postalCode: "",
    city: "",
    country: "",
  };

  if (required) {
    if (a.street.trim() === "") errors.street = REQUIRED_MESSAGE;
    if (a.postalCode.trim() === "") errors.postalCode = REQUIRED_MESSAGE;
    if (a.city.trim() === "") errors.city = REQUIRED_MESSAGE;
    if (a.country.trim() === "") errors.country = REQUIRED_MESSAGE;
  }
  // Like the CRM dialogs, an address counts only when picked from the
  // address search — that checks it exists and yields the coordinates.
  const hasContent = [a.street, a.postalCode, a.city].some(
    part => part.trim() !== ""
  );
  if (hasContent && !a.location)
    errors.location = ADDRESS_NOT_FROM_SEARCH_MESSAGE;
  if (
    !errors.postalCode &&
    a.postalCode.trim() !== "" &&
    isGermanCountry(a.country) &&
    !DE_POSTAL_CODE_RE.test(a.postalCode.trim())
  )
    errors.postalCode = INVALID_POSTAL_CODE_MESSAGE;

  return errors;
}

function valueToText(value: FormFieldValue): string {
  if (value === null) return "";
  if (Array.isArray(value)) return value.join(", ");
  if (isAddressValue(value)) return formatAddressValue(value);
  return String(value);
}

/**
 * One field's validation message for its current value, or `null` when it is
 * valid — required, email format, phone characters, a German postal code (5
 * digits), an ISO date and a number.
 */
export function validateFieldValue(
  field: Pick<FormField, "type" | "required">,
  value: FormFieldValue
): string | null {
  if (field.type === "address") {
    const errors = validateAddressValue(
      isAddressValue(value) ? value : null,
      field.required
    );
    const messages = Object.values(errors);
    return messages.length > 0 ? messages.join("; ") : null;
  }

  if (field.type === "multiselect") {
    const list = Array.isArray(value) ? value : [];
    return field.required && list.length === 0 ? REQUIRED_MESSAGE : null;
  }

  const text = valueToText(value);
  if (field.required && text.trim() === "") return REQUIRED_MESSAGE;
  if (text.trim() === "") return null;

  switch (field.type) {
    case "email":
      return EMAIL_RE.test(text) ? null : INVALID_EMAIL_MESSAGE;
    case "phone":
      return PHONE_RE.test(text) ? null : INVALID_PHONE_MESSAGE;
    case "date":
      return isIsoDate(text) ? null : INVALID_DATE_MESSAGE;
    case "number":
      return NUMBER_RE.test(text.trim()) ? null : INVALID_NUMBER_MESSAGE;
    default:
      return null;
  }
}

function isIsoDate(text: string): boolean {
  if (!ISO_DATE_RE.test(text)) return false;
  const date = new Date(`${text}T00:00:00Z`);
  return (
    !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === text
  );
}

/**
 * Whether the spec shows a create (every field's `previous` is `null`)
 * rather than an edit — an edit's changed fields are highlighted, a
 * create's never are.
 */
export function isCreateForm(view: Pick<FormViewSpec, "fields">): boolean {
  return view.fields.every(field => field.previous === null);
}

function valuesEqual(a: FormFieldValue, b: FormFieldValue): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b))
    return a.length === b.length && a.every((v, i) => v === b[i]);
  if (isAddressValue(a) && isAddressValue(b))
    return (
      a.street === b.street &&
      a.postalCode === b.postalCode &&
      a.city === b.city &&
      a.country === b.country
    );
  return false;
}

/** Whether a field's current value differs from `previous` (pure comparison — see `isCreateForm` for whether to act on it). */
export function isFieldChanged(
  value: FormFieldValue,
  previous: FormFieldValue
): boolean {
  return !valuesEqual(value, previous);
}

/**
 * Turns what an input control gave back into the field's canonical value: a
 * number field's text becomes a JS number (or stays as the invalid text so
 * `validateFieldValue` can still report it), and blank text becomes `null`.
 * Arrays and addresses pass through unchanged.
 */
export function coerceFieldValue(
  type: FormFieldType,
  raw: FormFieldValue
): FormFieldValue {
  if (type === "number") {
    if (raw === null) return null;
    const text = typeof raw === "string" ? raw.trim() : String(raw);
    if (text === "") return null;
    const parsed = Number(text);
    return Number.isNaN(parsed) ? raw : parsed;
  }
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    return trimmed === "" ? null : trimmed;
  }
  return raw;
}

/** "Street, postal-code City", or "" when every part is empty. */
export function formatAddressValue(address: Address): string {
  if (!address.street && !address.postalCode && !address.city) return "";
  return `${address.street}, ${address.postalCode} ${address.city}`.trim();
}

/** A field's value, formatted for display: option labels, "Street, postal-code City", "–" for empty. */
export function formValueText(
  field: Pick<FormField, "type" | "options">,
  value: FormFieldValue
): string {
  if (value === null) return "–";
  if (isAddressValue(value)) {
    const text = formatAddressValue(value);
    return text === "" ? "–" : text;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return "–";
    return value
      .map(v => field.options?.find(o => o.value === v)?.label ?? v)
      .join(", ");
  }
  if (typeof value === "string") {
    if (value.trim() === "") return "–";
    if (field.type === "select")
      return field.options?.find(o => o.value === value)?.label ?? value;
    return value;
  }
  return String(value);
}

function resolvedValue(
  field: FormField,
  values: Record<string, FormFieldValue>
): FormFieldValue {
  return Object.prototype.hasOwnProperty.call(values, field.key)
    ? values[field.key]
    : (field.value ?? null);
}

/**
 * The exact text sent as the user's chat message: `<title>: <label>:
 * <value>; …`, values falling back to the field's prefilled value when the
 * submission did not include that key. `<title>` is the assistant's own
 * (dynamic, whatever-language) form title — not translated here.
 */
export function buildFormAnswerText(
  view: Pick<FormViewSpec, "title" | "fields">,
  values: Record<string, FormFieldValue>
): string {
  const parts = view.fields.map(
    field =>
      `${field.label}: ${formValueText(field, resolvedValue(field, values))}`
  );
  return `${view.title}: ${parts.join("; ")}`;
}

/** The machine payload `{ key: value }` a submit hands the backend, exact values. */
export function buildFormAnswerPayload(
  view: Pick<FormViewSpec, "fields">,
  values: Record<string, FormFieldValue>
): Record<string, FormFieldValue> {
  const payload: Record<string, FormFieldValue> = {};
  for (const field of view.fields)
    payload[field.key] = resolvedValue(field, values);
  return payload;
}

const FORM_ANSWER_FENCE_LANG = "agent-form";
const FORM_ANSWER_BLOCK_RE = new RegExp(
  "\\n\\n```" + FORM_ANSWER_FENCE_LANG + "\\n([\\s\\S]*?)\\n```\\s*$"
);

/**
 * The full message a submit sends: the human-readable answer line, then the
 * machine payload as a trailing fenced block (see the module doc for why).
 */
export function buildFormAnswerMessage(
  view: FormViewSpec,
  values: Record<string, FormFieldValue>
): string {
  const text = buildFormAnswerText(view, values);
  const payload = buildFormAnswerPayload(view, values);
  return appendFormAnswerBlock(text, payload);
}

/** `text` followed by `payload` as the trailing machine block; other views reuse the same fence. */
export function appendFormAnswerBlock(text: string, payload: object): string {
  return `${text}\n\n\`\`\`${FORM_ANSWER_FENCE_LANG}\n${JSON.stringify(payload)}\n\`\`\``;
}

/** The message as shown to the user: the machine block removed. */
export function stripFormAnswerBlock(text: string): string {
  return text.replace(FORM_ANSWER_BLOCK_RE, "").trimEnd();
}

/** The machine payload a message's trailing fenced block carries, or `null` when there is none (or it does not parse). */
export function extractFormAnswerPayload(
  text: string
): Record<string, FormFieldValue> | null {
  const match = text.match(FORM_ANSWER_BLOCK_RE);
  const json = match?.[1];
  if (!json) return null;
  try {
    const parsed: unknown = JSON.parse(json);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed))
      return null;
    return parsed as Record<string, FormFieldValue>;
  } catch {
    return null;
  }
}
